import type { Trip, Day } from '@my-fullstack-app/itinerary-types';
import { useItineraryStore } from '../store/itinerary-store';
import { addDays, todayPlainDate, toIsoDateString } from './dates';

/** Stable ID for the demo trip so we can check if it exists and avoid duplicates. */
export const DEMO_TRIP_ID = 'demo-fukuoka-japan';

/** Today-relative dates so the demo trip stays inside the OpenWeather
 *  5-day forecast horizon — otherwise the weather chips silently render
 *  nothing and users assume the wiring is broken. */
function demoDates(): [string, string, string, string, string] {
  const today = todayPlainDate();
  return [
    toIsoDateString(today),
    toIsoDateString(addDays(today, 1)),
    toIsoDateString(addDays(today, 2)),
    toIsoDateString(addDays(today, 3)),
    toIsoDateString(addDays(today, 4)),
  ];
}

/**
 * Seeds the demo itinerary into local storage when the user has no trips.
 * Call for anonymous users so "My Trips" already shows the demo (e.g. on landing or before navigating to /trips).
 *
 * Also re-seeds when an existing demo trip's date range has slid out of
 * the forecast horizon (i.e. left over from a previous session) — keeps
 * weather chips populated without the user having to wipe local storage.
 */
export function ensureDemoTripForAnonymous(): void {
  const { trips, importTrips, deleteTrip } = useItineraryStore.getState();
  const existing = trips.find((t) => t.id === DEMO_TRIP_ID);
  const today = toIsoDateString(todayPlainDate());
  if (trips.length === 0) {
    importTrips([getDemoTrip()]);
  } else if (existing && existing.endDate < today) {
    deleteTrip(DEMO_TRIP_ID);
    importTrips([getDemoTrip()]);
  }
}

/** Pre-built 5-day "Fukuoka, Japan" sample itinerary for the demo. */
export function getDemoTrip(): Trip {
  const d1 = 'demo-d1';
  const d2 = 'demo-d2';
  const d3 = 'demo-d3';
  const d4 = 'demo-d4';
  const d5 = 'demo-d5';
  const [date1, date2, date3, date4, date5] = demoDates();

  const day1: Day = {
    id: d1,
    date: date1,

    hours: '09:00 – 21:00',
    places: [
      {
        id: 'demo-p1a',
        placeId: 'p1a',
        name: 'Momochi Seaside Park',
        address: 'Momochihama, Sawara Ward, Fukuoka',
        lat: 33.5845,
        lng: 130.3512,
        startTime: '09:00',
        endTime: '10:30',
      },
      {
        id: 'demo-p1b',
        placeId: 'p1b',
        name: 'Fukuoka Tower',
        address: '2 Chome-3-26 Momochihama, Sawara Ward, Fukuoka',
        lat: 33.5904,
        lng: 130.3578,
        startTime: '10:45',
        endTime: '12:30',
      },
      {
        id: 'demo-p1c',
        placeId: 'p1c',
        name: 'Canal City Hakata',
        address: '1-2 Sumiyoshi, Hakata Ward, Fukuoka',
        lat: 33.589,
        lng: 130.4112,
        startTime: '14:00',
        endTime: '16:30',
      },
      {
        id: 'demo-p1d',
        placeId: 'p1d',
        name: 'Kawabata Shotengai',
        address: 'Kawabata, Hakata Ward, Fukuoka',
        lat: 33.5933,
        lng: 130.4078,
        startTime: '17:00',
        endTime: '19:00',
      },
    ],
    transports: [
      {
        id: 'demo-t1a',
        mode: 'walk',
        startTime: '10:30',
        endTime: '10:45',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p1a',
        toPlaceId: 'demo-p1b',
      },
      {
        id: 'demo-t1b',
        mode: 'train',
        startTime: '12:30',
        endTime: '13:45',
        notes: 'Nishitetsu to Hakata',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p1b',
        toPlaceId: 'demo-p1c',
      },
      {
        id: 'demo-t1c',
        mode: 'walk',
        startTime: '16:30',
        endTime: '16:45',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p1c',
        toPlaceId: 'demo-p1d',
      },
    ],
    customs: [],
  };

  const day2: Day = {
    id: d2,
    date: date2,

    hours: '08:00 – 19:00',
    places: [
      {
        id: 'demo-p2a',
        placeId: 'p2a',
        name: 'Dazaifu Tenmangu',
        address: '4-7-1 Saifu, Dazaifu, Fukuoka',
        lat: 33.5175,
        lng: 130.5348,
        startTime: '09:00',
        endTime: '11:00',
      },
      {
        id: 'demo-p2b',
        placeId: 'p2b',
        name: 'Starbucks Dazaifu',
        address: '3-2-43 Saifu, Dazaifu, Fukuoka',
        lat: 33.5165,
        lng: 130.5315,
        startTime: '11:15',
        endTime: '12:00',
      },
      {
        id: 'demo-p2c',
        placeId: 'p2c',
        name: 'Kyushu National Museum',
        address: '4-7-2 Ishizaka, Dazaifu, Fukuoka',
        lat: 33.5158,
        lng: 130.5372,
        startTime: '12:30',
        endTime: '15:00',
      },
      {
        id: 'demo-p2d',
        placeId: 'p2d',
        name: 'Komyozenji Temple',
        address: '2-16-1 Zasshonohana, Dazaifu, Fukuoka',
        lat: 33.5122,
        lng: 130.5289,
        startTime: '15:30',
        endTime: '16:30',
      },
    ],
    transports: [
      {
        id: 'demo-t2a',
        mode: 'train',
        startTime: '08:00',
        endTime: '08:45',
        notes: 'Nishitetsu Dazaifu Line',
      },
      {
        id: 'demo-t2b',
        mode: 'walk',
        startTime: '11:00',
        endTime: '11:15',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p2a',
        toPlaceId: 'demo-p2b',
      },
      {
        id: 'demo-t2c',
        mode: 'walk',
        startTime: '12:00',
        endTime: '12:30',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p2b',
        toPlaceId: 'demo-p2c',
      },
      {
        id: 'demo-t2d',
        mode: 'walk',
        startTime: '15:00',
        endTime: '15:30',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p2c',
        toPlaceId: 'demo-p2d',
      },
    ],
    customs: [],
  };

  const day3: Day = {
    id: d3,
    date: date3,

    hours: '08:00 – 19:00',
    places: [
      {
        id: 'demo-p3a',
        placeId: 'p3a',
        name: 'Ohori Park',
        address: '1 Ohorikoen, Chuo Ward, Fukuoka',
        lat: 33.5892,
        lng: 130.3752,
        startTime: '09:00',
        endTime: '11:00',
      },
      {
        id: 'demo-p3b',
        placeId: 'p3b',
        name: 'Fukuoka Art Museum',
        address: '1-6 Ohorikoen, Chuo Ward, Fukuoka',
        lat: 33.5885,
        lng: 130.3789,
        startTime: '11:15',
        endTime: '12:30',
      },
      {
        id: 'demo-p3c',
        placeId: 'p3c',
        name: 'Fukuoka Castle Ruins',
        address: 'Jonai, Chuo Ward, Fukuoka',
        lat: 33.5839,
        lng: 130.3833,
        startTime: '14:00',
        endTime: '15:30',
      },
      {
        id: 'demo-p3d',
        placeId: 'p3d',
        name: 'Maizuru Park & Gokoku Shrine',
        address: 'Jonai, Chuo Ward, Fukuoka',
        lat: 33.5842,
        lng: 130.3811,
        startTime: '15:45',
        endTime: '17:00',
      },
    ],
    transports: [
      {
        id: 'demo-t3a',
        mode: 'walk',
        startTime: '11:00',
        endTime: '11:15',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p3a',
        toPlaceId: 'demo-p3b',
      },
      {
        id: 'demo-t3b',
        mode: 'walk',
        startTime: '12:30',
        endTime: '13:45',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p3b',
        toPlaceId: 'demo-p3c',
      },
      {
        id: 'demo-t3c',
        mode: 'walk',
        startTime: '15:30',
        endTime: '15:45',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p3c',
        toPlaceId: 'demo-p3d',
      },
    ],
    customs: [],
  };

  const day4: Day = {
    id: d4,
    date: date4,

    hours: '08:00 – 21:00',
    places: [
      {
        id: 'demo-p4a',
        placeId: 'p4a',
        name: 'Yanagawa River Cruising',
        address: 'Yanagawa, Fukuoka',
        lat: 33.1667,
        lng: 130.4,
        startTime: '10:00',
        endTime: '12:00',
      },
      {
        id: 'demo-p4b',
        placeId: 'p4b',
        name: 'Unagi Lunch in Yanagawa',
        address: 'Yanagawa, Fukuoka',
        lat: 33.1689,
        lng: 130.4022,
        startTime: '12:30',
        endTime: '13:30',
      },
      {
        id: 'demo-p4c',
        placeId: 'p4c',
        name: 'Ohana (flower garden)',
        address: 'Yanagawa, Fukuoka',
        lat: 33.1655,
        lng: 130.3989,
        startTime: '14:00',
        endTime: '15:30',
      },
      {
        id: 'demo-p4d',
        placeId: 'p4d',
        name: 'Nakasu Yatai Stalls',
        address: 'Nakasu, Hakata Ward, Fukuoka',
        lat: 33.5934,
        lng: 130.4016,
        startTime: '18:30',
        endTime: '20:30',
      },
    ],
    transports: [
      {
        id: 'demo-t4a',
        mode: 'train',
        startTime: '08:00',
        endTime: '09:30',
        notes: 'JR to Yanagawa',
      },
      {
        id: 'demo-t4b',
        mode: 'walk',
        startTime: '12:00',
        endTime: '12:30',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p4a',
        toPlaceId: 'demo-p4b',
      },
      {
        id: 'demo-t4c',
        mode: 'walk',
        startTime: '13:30',
        endTime: '14:00',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p4b',
        toPlaceId: 'demo-p4c',
      },
      {
        id: 'demo-t4d',
        mode: 'train',
        startTime: '16:00',
        endTime: '17:30',
        notes: 'Return to Fukuoka',
      },
    ],
    customs: [],
  };

  const day5: Day = {
    id: d5,
    date: date5,

    hours: '08:00 – 16:00',
    places: [
      {
        id: 'demo-p5a',
        placeId: 'p5a',
        name: 'Kushida Shrine',
        address: '1-41 Kamikawabata-machi, Hakata Ward, Fukuoka',
        lat: 33.5897,
        lng: 130.4094,
        startTime: '09:00',
        endTime: '10:00',
      },
      {
        id: 'demo-p5b',
        placeId: 'p5b',
        name: 'Ichiran Ramen (Hakata)',
        address: 'Hakata Station, Fukuoka',
        lat: 33.5894,
        lng: 130.4202,
        startTime: '10:30',
        endTime: '11:15',
      },
      {
        id: 'demo-p5c',
        placeId: 'p5c',
        name: 'Hakata Station DEPA',
        address: 'Hakata Station, Fukuoka',
        lat: 33.5898,
        lng: 130.4215,
        startTime: '11:30',
        endTime: '12:30',
      },
      {
        id: 'demo-p5d',
        placeId: 'p5d',
        name: 'Fukuoka Airport',
        address: 'Fukuoka Airport, Fukuoka',
        lat: 33.5859,
        lng: 130.451,
        startTime: '14:00',
        endTime: '16:00',
      },
    ],
    transports: [
      {
        id: 'demo-t5a',
        mode: 'walk',
        startTime: '10:00',
        endTime: '10:30',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p5a',
        toPlaceId: 'demo-p5b',
      },
      {
        id: 'demo-t5b',
        mode: 'walk',
        startTime: '11:15',
        endTime: '11:30',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p5b',
        toPlaceId: 'demo-p5c',
      },
      {
        id: 'demo-t5c',
        mode: 'metro',
        startTime: '12:30',
        endTime: '13:15',
        notes: 'Kuko Line to Airport',
        isAutoGenerated: true,
        fromPlaceId: 'demo-p5c',
        toPlaceId: 'demo-p5d',
      },
    ],
    customs: [],
  };

  return {
    id: DEMO_TRIP_ID,
    name: 'Fukuoka & Kyushu',
    destination: 'Fukuoka, Japan',
    startDate: date1,
    endDate: date5,
    days: [day1, day2, day3, day4, day5],
    // Keep the sample local-only so it never lands in a real user's
    // account when they sign in or claim anonymous trips.
    isDemo: true,
  };
}
